import React, { useEffect, useState } from 'react'
import { Alert, Button, Card, CardBody, Col, FormGroup, Input, Label, Row } from 'reactstrap'
import toastr from 'toastr'

import ApiService from '../../data/services/ApiService'

const LABELS = {
    FA: {
        title: 'Facturas',
        description: 'También se utiliza para las notas de crédito emitidas desde el flujo de facturas.'
    },
    PA: {
        title: 'Complementos de pago',
        description: 'Numeración independiente para CFDI con complemento de pagos.'
    }
}

const Folios = () => {
    const [counters, setCounters] = useState([])
    const [values, setValues] = useState({})
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState('')
    const [error, setError] = useState('')

    const loadFolios = async () => {
        try {
            setLoading(true)
            setError('')
            const response = await ApiService.getFolios()
            const loadedCounters = response.data.data.counters || []
            setCounters(loadedCounters)
            setValues(loadedCounters.reduce((result, counter) => ({
                ...result,
                [counter.tipoMovimiento]: String(counter.nextFolio)
            }), {}))
        } catch (requestError) {
            setError(requestError.response?.data?.message || 'No pude cargar la configuración de folios.')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadFolios()
    }, [])

    const save = async (counter) => {
        const nextFolio = Number(values[counter.tipoMovimiento])
        if (!Number.isSafeInteger(nextFolio) || nextFolio < 1) {
            toastr.error('El próximo folio debe ser un número entero mayor a cero.')
            return
        }

        if (nextFolio < counter.nextFolio) {
            toastr.error(`No puedes reducir el folio por debajo de ${counter.nextFolio}.`)
            return
        }

        if (nextFolio === counter.nextFolio) return

        const label = LABELS[counter.tipoMovimiento]?.title.toLowerCase()
        if (!window.confirm(`El próximo folio de ${label} será ${nextFolio}. ¿Deseas continuar?`)) {
            return
        }

        try {
            setSaving(counter.tipoMovimiento)
            const response = await ApiService.updateFolio(counter.tipoMovimiento, {
                nextFolio,
                expectedCurrentFolio: counter.currentFolio
            })
            const updated = response.data.data
            setCounters(current => current.map(item => (
                item.tipoMovimiento === updated.tipoMovimiento ? updated : item
            )))
            setValues(current => ({ ...current, [updated.tipoMovimiento]: String(updated.nextFolio) }))
            toastr.success(`El próximo folio será ${updated.nextFolio}.`)
        } catch (requestError) {
            const data = requestError.response?.data
            toastr.error(data?.message || 'No pude actualizar el folio.')

            if (data?.data?.nextFolio) {
                await loadFolios()
            }
        } finally {
            setSaving('')
        }
    }

    return (
        <div>
            <Card>
                <CardBody>
                    <h3 style={{ marginBottom: 4 }}>Folios</h3>
                    <p style={{ color: '#6c757d', marginBottom: 20 }}>
                        Define el número que recibirá el próximo comprobante timbrado.
                    </p>

                    <Alert color="warning">
                        Solo puedes avanzar la numeración. Una factura programada u otro usuario podría usar el próximo folio antes que tú.
                    </Alert>

                    {error && <Alert color="danger">{error}</Alert>}
                    {loading && <p>Cargando folios...</p>}

                    {!loading && !error && (
                        <Row>
                            {counters.map(counter => {
                                const copy = LABELS[counter.tipoMovimiento]
                                const numericValue = Number(values[counter.tipoMovimiento])
                                const invalid = !Number.isSafeInteger(numericValue) || numericValue < counter.nextFolio
                                const unchanged = numericValue === counter.nextFolio

                                return (
                                    <Col md={6} xs={12} key={counter.tipoMovimiento}>
                                        <Card style={{ height: '100%' }}>
                                            <CardBody>
                                                <h4>{copy.title}</h4>
                                                <p style={{ color: '#6c757d', minHeight: 42 }}>{copy.description}</p>
                                                <FormGroup>
                                                    <Label for={`folio-${counter.tipoMovimiento}`}>Próximo folio</Label>
                                                    <Input
                                                        id={`folio-${counter.tipoMovimiento}`}
                                                        type="number"
                                                        min={counter.nextFolio}
                                                        step="1"
                                                        value={values[counter.tipoMovimiento] || ''}
                                                        onChange={event => setValues(current => ({
                                                            ...current,
                                                            [counter.tipoMovimiento]: event.target.value
                                                        }))}
                                                    />
                                                    <small className="text-muted">
                                                        Folio actual almacenado: {counter.currentFolio}
                                                    </small>
                                                </FormGroup>
                                                <Button
                                                    color="primary"
                                                    disabled={invalid || unchanged || Boolean(saving)}
                                                    onClick={() => save(counter)}
                                                >
                                                    {saving === counter.tipoMovimiento ? 'Guardando...' : 'Guardar próximo folio'}
                                                </Button>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                )
                            })}
                        </Row>
                    )}
                </CardBody>
            </Card>
        </div>
    )
}

export default Folios
